import { Box, Modal, Typography, Chip, IconButton } from "@mui/material";
import { Stack } from "@mui/system";
import { CloseIcon } from "common-lib";
import { Rnd } from "react-rnd";
import { centeredRect } from "../../utils/utils";
import EpochTrainModelTable, { EpochModelRow } from "./EpochTrainModelTable";
import { useEffect, useMemo, useState } from "react";
import { InspModelItem } from "@types";
import { promoteInspModel } from "../../services/modelService";
import { useParsedModels } from "../../hooks/useParsedModels";
import { useHistoryMap } from "../../hooks/useHistoryMap";
import { useEpochList } from "../../hooks/useEpochList";
import { getEpochModels } from "../../services/epochModelService";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

interface CompareModalProps {
  openCompare: boolean;
  setOpenCompare: (open: boolean) => void;
  epochList: any[];
  inspModelList: InspModelItem[];
  onSaved: () => void;
}

export default function ModelCompareModal({ openCompare, setOpenCompare, inspModelList, onSaved }: CompareModalProps) {
  const { id } = useParams();
  const [tempModelList, setTempModelList] = useState<InspModelItem[]>([]);
  const parsedTempModels = useParsedModels(tempModelList);
  const tempHistoryMap = useHistoryMap(parsedTempModels);
  const tempEpochList = useEpochList(parsedTempModels);

  const [compareRound, setCompareRound] = useState<number | null>(null);

  const fetchTempModels = async () => {
    try {
      const response = await getEpochModels({
        project_id: Number(id),
      });
      setTempModelList(response.data.contents);
      console.log("response", response);
    } catch (error) {
      console.error("Error fetching epoch models:", error);
    }
  };

  useEffect(() => {
    fetchTempModels();
  }, []);

  // const compareData: EpochModelRow[] = tempEpochList
  //   .filter((item) => compareRound === null || item.historyIndex === compareRound)
  //   .map((item) => ({
  //     ...item,
  //     isSaved: inspModelList.some((m) => m.id === item.id),
  //     created_at: item.created_at,
  //   }));

  // inspModelList를 EpochModelRow 형태로 변환
  const parsedInspModels = useParsedModels(inspModelList);
  const inspEpochList = useEpochList(parsedInspModels);

  const compareData: EpochModelRow[] = useMemo(() => {
    const tempRows = tempEpochList.map((item) => ({
      ...item,
      isSaved: inspModelList.some((m) => m.uuid === item.uuid),
    }));

    const inspRows = inspEpochList
      .filter((item) => !tempRows.some((t) => t.uuid === item.uuid))
      .map((item) => ({
        ...item,
        isSaved: true,
      }));

    const merged = [...tempRows, ...inspRows];

    return merged;
  }, [tempEpochList, inspEpochList, compareRound]);

  const handleSaveCompare = async (selectedIds: number[]) => {
    await promoteInspModel(selectedIds);
    onSaved();
    toast.success("성공적으로 검사모델로 저장하였습니다.");
  };

  return (
    <Modal
      id="modal-model-compare"
      open={openCompare}
      sx={{ pointerEvents: "none" }}
      onClose={() => setOpenCompare(false)}
      disableEnforceFocus
      disableAutoFocus
      hideBackdrop
      disableRestoreFocus
    >
      <div>
        <Rnd
          default={centeredRect(820, 520)}
          minWidth={640}
          minHeight={380}
          bounds="window"
          dragHandleClassName="compare-drag-handle"
          style={{ pointerEvents: "auto" }}
        >
          <Box
            sx={{
              width: "100%",
              height: "100%",
              bgcolor: "background.paper",
              boxShadow: 24,
              borderRadius: 2,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box
              className="compare-drag-handle"
              sx={{
                cursor: "move",
                borderBottom: "1px solid var(--dk-border, #ddd)",
                padding: "8px 16px",
                position: "relative",
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h5" fontWeight={700}>
                  Model Compare
                </Typography>
                {compareRound !== null && (
                  <Chip label={`Round ${compareRound}`} size="small" sx={{ height: 20, fontSize: 11 }} />
                )}
                <Chip
                  label="임시 저장 · 자동 삭제"
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ height: 20, fontSize: 11 }}
                />
              </Stack>

              <IconButton
                id="close-modal-model-compare"
                onClick={() => setOpenCompare(false)}
                sx={{ position: "absolute", right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 2 }}>
              <EpochTrainModelTable data={compareData} onSave={handleSaveCompare} setOpenCompare={setOpenCompare} />
            </Box>
          </Box>
        </Rnd>
      </div>
    </Modal>
  );
}
